import { ReactElement, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { StiffeningMesh } from '@scene'
import { AxiosError } from 'axios'
import { augmentProposal } from '@editor/stores/structuralPlanningStore/utils'
import { useQuery } from '@tanstack/react-query'
import { enqueueSnackbar } from '@ui/snackbar'
import { useControlStore } from '@editorStores'
import { useHiddenElements } from '@editorHooks'
import { getStiffeningIntervals } from '@queries'
import { buildErrorMessage } from 'src/constants/errors'

/**
 * Shows the stiffening segments on the global loads page. They carry the storey
 * stiffnesses the seismic substitute forces are distributed by, so they have to
 * be visible here even though this page cannot edit them.
 *
 * Structural planning keeps the segments in the stiffening store, but the global
 * loads page runs none of its queries, so they are fetched here. Rendering is
 * read-only: clicks pass through to the elements underneath.
 */
const GlobalLoadsStiffeningMesh = (): ReactElement | null => {
  const { projectId }: { projectId?: string } = useParams()

  const isVisible = useControlStore(state => state.structuralPlanningElementVisibility.stiffening)
  const hiddenElements = useHiddenElements()

  const { data } = useQuery<StiffeningSegment[], AxiosError>({
    queryKey: getStiffeningIntervals.getKey(projectId),
    queryFn: () => getStiffeningIntervals.request(projectId),
    onError: (error: AxiosError) => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der aussteifenden Segmente'), {
        variant: 'error',
      })
    },
  })

  // The same augmentation the stiffening store applies. It is what defaults
  // `selectable`, and a segment without it is drawn at zero opacity - so raw
  // response data renders an invisible mesh.
  const stiffeningIntervals = useMemo(() => augmentProposal(data ?? []), [data])

  if (!isVisible || !stiffeningIntervals.length) return null

  return (
    <StiffeningMesh
      data={stiffeningIntervals}
      hiddenElements={hiddenElements}
      pointerPropagation
      noPointerInteractions
    />
  )
}

export default GlobalLoadsStiffeningMesh
